import { useEffect, useRef } from 'react';
import { useInboxSummary } from './api';

/**
 * Сигнал оператору (решение Игоря 29.10): красная точка в меню, красная точка
 * на значке вкладки и щелчок при новом сообщении — чтобы не пропустить клиента.
 * Возвращает число запросов, ждущих ответа.
 */
export function useOperatorSignal(): number {
  const { data } = useInboxSummary();
  const need = data?.needs_reply ?? 0;
  const last = data?.last_event_at ?? null;
  const seen = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    setFaviconDot(need > 0);
  }, [need]);

  useEffect(() => {
    if (data === undefined) return;
    // Первая загрузка — без звука; дальше щёлкаем на каждое новое событие.
    if (seen.current !== undefined && last && (!seen.current || last > seen.current)) playClick();
    seen.current = last;
  }, [data, last]);

  useEffect(() => () => setFaviconDot(false), []);
  return need;
}

// --- значок вкладки ----------------------------------------------------------

const original = new Map<HTMLLinkElement, { href: string; type: string }>();
let dotted: string | null = null;

function iconLinks(): HTMLLinkElement[] {
  return Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]'));
}

function setFaviconDot(on: boolean) {
  const links = iconLinks();
  if (!on) {
    original.forEach((o, link) => {
      link.href = o.href;
      link.type = o.type;
    });
    original.clear();
    return;
  }
  const apply = (url: string) =>
    links.forEach((l) => {
      if (!original.has(l)) original.set(l, { href: l.href, type: l.type });
      l.type = 'image/png';
      l.href = url;
    });
  if (dotted) {
    apply(dotted);
    return;
  }
  const img = new Image();
  img.onload = () => {
    const c = document.createElement('canvas');
    c.width = 64;
    c.height = 64;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(img, 0, 0, 64, 64);
    ctx.beginPath();
    ctx.arc(48, 16, 15, 0, Math.PI * 2);
    ctx.fillStyle = '#DC2626';
    ctx.fill();
    ctx.lineWidth = 4;
    ctx.strokeStyle = '#fff';
    ctx.stroke();
    dotted = c.toDataURL('image/png');
    apply(dotted);
  };
  img.src = '/favicon-32.png';
}

// --- щелчок ------------------------------------------------------------------

let audio: AudioContext | null = null;

function playClick() {
  try {
    audio ??= new AudioContext();
    if (audio.state === 'suspended') void audio.resume();
    const t = audio.currentTime;
    const osc = audio.createOscillator();
    const gain = audio.createGain();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(1600, t);
    osc.frequency.exponentialRampToValueAtTime(700, t + 0.05);
    gain.gain.setValueAtTime(0.35, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.08);
    osc.connect(gain).connect(audio.destination);
    osc.start(t);
    osc.stop(t + 0.09);
  } catch {
    /* браузер не дал звук без клика по странице — точки всё равно видны */
  }
}
